import {
  GigCartVerificationIssueReason,
  MAX_GIG_CART_ENTRIES,
  MAX_GIG_CART_PROMPT_EXAMPLE_CHARACTERS,
  MAX_GIG_CART_PROMPT_EXAMPLES,
} from '@/models/GigCartVerificationPromptConfig';

const issueReasons = new Set<string>(Object.values(GigCartVerificationIssueReason));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isValidString = (value: unknown, maxLength: number, allowEmpty = false): value is string =>
  typeof value === 'string' && value.length <= maxLength && (allowEmpty || Boolean(value.trim()));

const isNullableString = (value: unknown, maxLength: number): boolean =>
  value === null || isValidString(value, maxLength, true);

const isNonNegativeSafeInteger = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) >= 0;

const unexpectedKey = (value: Record<string, unknown>, allowedKeys: readonly string[]): string | undefined =>
  Object.keys(value).find((key) => !allowedKeys.includes(key));

export const validateGigCartVerificationPromptConfig = (config: unknown): string | null => {
  if (!isRecord(config)) {
    return 'Config must be a JSON object like { "examples": [...] }';
  }
  const unexpectedConfigKey = unexpectedKey(config, ['examples']);
  if (unexpectedConfigKey) {
    return `Config contains unsupported field "${unexpectedConfigKey}"`;
  }

  const examples = config['examples'];
  if (!Array.isArray(examples) || examples.length === 0 || examples.length > MAX_GIG_CART_PROMPT_EXAMPLES) {
    return `"examples" must contain between 1 and ${MAX_GIG_CART_PROMPT_EXAMPLES} entries`;
  }

  for (let exampleIndex = 0; exampleIndex < examples.length; exampleIndex += 1) {
    const example = examples[exampleIndex];
    if (!isRecord(example)) {
      return `examples[${exampleIndex}] must be an object`;
    }
    const unexpectedExampleKey = unexpectedKey(example, ['input', 'expected']);
    if (unexpectedExampleKey) {
      return `examples[${exampleIndex}] contains unsupported field "${unexpectedExampleKey}"`;
    }
    if (JSON.stringify(example).length > MAX_GIG_CART_PROMPT_EXAMPLE_CHARACTERS) {
      return `examples[${exampleIndex}] must not exceed ${MAX_GIG_CART_PROMPT_EXAMPLE_CHARACTERS.toLocaleString()} characters`;
    }

    const input = example['input'];
    const expected = example['expected'];
    if (!isRecord(input)) {
      return `examples[${exampleIndex}].input must be an object`;
    }
    const unexpectedInputKey = unexpectedKey(input, ['orderLines', 'cartHtml']);
    if (unexpectedInputKey) {
      return `examples[${exampleIndex}].input contains unsupported field "${unexpectedInputKey}"`;
    }
    const orderLines = input['orderLines'];
    const cartHtml = input['cartHtml'];
    if (!Array.isArray(orderLines) || orderLines.length === 0 || orderLines.length > MAX_GIG_CART_ENTRIES) {
      return `examples[${exampleIndex}].input.orderLines must contain between 1 and ${MAX_GIG_CART_ENTRIES} entries`;
    }
    if (!isValidString(cartHtml, Number.MAX_SAFE_INTEGER)) {
      return `examples[${exampleIndex}].input.cartHtml must be a non-empty string`;
    }

    const knownItemIds = new Set<string>();
    const orderLineDetails = new Map<string, { sku: string | null; requiredUnits: number }>();
    for (let orderLineIndex = 0; orderLineIndex < orderLines.length; orderLineIndex += 1) {
      const orderLine = orderLines[orderLineIndex];
      const path = `examples[${exampleIndex}].input.orderLines[${orderLineIndex}]`;
      if (!isRecord(orderLine)) {
        return `${path} must be an object`;
      }
      const unexpectedOrderLineKey = unexpectedKey(orderLine, [
        'itemId',
        'sku',
        'requiredUnits',
        'displayName',
        'originalName',
        'productSize',
      ]);
      if (unexpectedOrderLineKey) {
        return `${path} contains unsupported field "${unexpectedOrderLineKey}"`;
      }
      const itemId = orderLine['itemId'];
      if (!isValidString(itemId, 64)) {
        return `${path}.itemId must be a non-empty string of at most 64 characters`;
      }
      if (knownItemIds.has(itemId)) {
        return `${path}.itemId must be unique within the example`;
      }
      knownItemIds.add(itemId);
      if (!isNullableString(orderLine['sku'], 500)) {
        return `${path}.sku must be a string or null`;
      }
      if (!Number.isSafeInteger(orderLine['requiredUnits']) || (orderLine['requiredUnits'] as number) <= 0) {
        return `${path}.requiredUnits must be a positive integer`;
      }
      if (!isValidString(orderLine['displayName'], 500)) {
        return `${path}.displayName must be a non-empty string`;
      }
      if (!isNullableString(orderLine['originalName'], 500)) {
        return `${path}.originalName must be a string or null`;
      }
      if (!isNullableString(orderLine['productSize'], 500)) {
        return `${path}.productSize must be a string or null`;
      }
      orderLineDetails.set(itemId, {
        sku: orderLine['sku'] as string | null,
        requiredUnits: orderLine['requiredUnits'] as number,
      });
    }

    if (!isRecord(expected)) {
      return `examples[${exampleIndex}].expected must be an object`;
    }
    const unexpectedExpectedKey = unexpectedKey(expected, ['cartLines', 'missingItemIds']);
    if (unexpectedExpectedKey) {
      return `examples[${exampleIndex}].expected contains unsupported field "${unexpectedExpectedKey}"`;
    }
    const cartLines = expected['cartLines'];
    const expectedMissingItemIds = expected['missingItemIds'];
    if (!Array.isArray(cartLines) || cartLines.length === 0 || cartLines.length > MAX_GIG_CART_ENTRIES) {
      return `examples[${exampleIndex}].expected.cartLines must contain between 1 and ${MAX_GIG_CART_ENTRIES} entries`;
    }
    if (!Array.isArray(expectedMissingItemIds) || expectedMissingItemIds.length > MAX_GIG_CART_ENTRIES) {
      return `examples[${exampleIndex}].expected.missingItemIds must be an array of at most ${MAX_GIG_CART_ENTRIES} entries`;
    }

    const assignedItemIds = new Set<string>();
    const cartLineDetails: Array<{ itemId?: string; totalUnits: number; lineTotalKrw: number; hasIssues: boolean }> =
      [];
    for (let cartLineIndex = 0; cartLineIndex < cartLines.length; cartLineIndex += 1) {
      const cartLine = cartLines[cartLineIndex];
      const path = `examples[${exampleIndex}].expected.cartLines[${cartLineIndex}]`;
      if (!isRecord(cartLine)) {
        return `${path} must be an object`;
      }
      const unexpectedCartLineKey = unexpectedKey(cartLine, [
        'productName',
        'option',
        'unitsPerListing',
        'cartQuantity',
        'lineTotalKrw',
        'itemId',
        'issues',
      ]);
      if (unexpectedCartLineKey) {
        return `${path} contains unsupported field "${unexpectedCartLineKey}"`;
      }
      if (!isValidString(cartLine['productName'], 500)) {
        return `${path}.productName must be a non-empty string`;
      }
      if (!isValidString(cartLine['option'], 500, true)) {
        return `${path}.option must be a string`;
      }
      if (!isNonNegativeSafeInteger(cartLine['unitsPerListing'])) {
        return `${path}.unitsPerListing must be a non-negative integer`;
      }
      if (!isNonNegativeSafeInteger(cartLine['cartQuantity'])) {
        return `${path}.cartQuantity must be a non-negative integer`;
      }
      if (!isNonNegativeSafeInteger(cartLine['lineTotalKrw'])) {
        return `${path}.lineTotalKrw must be a non-negative integer`;
      }
      const issues = cartLine['issues'];
      if (!Array.isArray(issues) || issues.some((issue) => typeof issue !== 'string' || !issueReasons.has(issue))) {
        return `${path}.issues contains an unsupported issue reason`;
      }
      if (new Set(issues).size !== issues.length) {
        return `${path}.issues must not contain duplicates`;
      }
      const itemId = cartLine['itemId'];
      if (itemId !== undefined) {
        if (!isValidString(itemId, 64) || !knownItemIds.has(itemId)) {
          return `${path}.itemId must reference an input order line`;
        }
        assignedItemIds.add(itemId);
      }
      cartLineDetails.push({
        ...(itemId === undefined ? {} : { itemId }),
        totalUnits: (cartLine['unitsPerListing'] as number) * (cartLine['cartQuantity'] as number),
        lineTotalKrw: cartLine['lineTotalKrw'] as number,
        hasIssues: issues.length > 0,
      });
    }

    const missingItemIds = new Set<string>();
    for (let missingItemIndex = 0; missingItemIndex < expectedMissingItemIds.length; missingItemIndex += 1) {
      const itemId = expectedMissingItemIds[missingItemIndex];
      const path = `examples[${exampleIndex}].expected.missingItemIds[${missingItemIndex}]`;
      if (!isValidString(itemId, 64) || !knownItemIds.has(itemId)) {
        return `${path} must reference an input order line`;
      }
      if (missingItemIds.has(itemId)) {
        return `${path} must not be duplicated`;
      }
      if (assignedItemIds.has(itemId)) {
        return `${path} must not also be assigned to a cart line`;
      }
      missingItemIds.add(itemId);
    }

    const allCartLinesAssigned = cartLineDetails.every(({ itemId }) => itemId !== undefined);
    const everyOrderLineAccountedFor = [...knownItemIds].every(
      (itemId) => assignedItemIds.has(itemId) || missingItemIds.has(itemId),
    );
    if (allCartLinesAssigned && !everyOrderLineAccountedFor) {
      return `examples[${exampleIndex}].expected must assign or mark every input itemId as missing when every cart line is assigned`;
    }

    const cleanResult = cartLineDetails.every(({ hasIssues }) => !hasIssues) && missingItemIds.size === 0;
    if (cleanResult && !allCartLinesAssigned) {
      return `examples[${exampleIndex}].expected must assign every clean cart line to an input itemId`;
    }

    if (cleanResult && allCartLinesAssigned) {
      const totalsByGroup = new Map<string, { requiredUnits: number; actualUnits: number; lineTotalKrw: number }>();
      for (const [itemId, orderLine] of orderLineDetails) {
        const groupKey = orderLine.sku === null ? `item:${itemId}` : `sku:${orderLine.sku}`;
        const current = totalsByGroup.get(groupKey) ?? { requiredUnits: 0, actualUnits: 0, lineTotalKrw: 0 };
        current.requiredUnits += orderLine.requiredUnits;
        totalsByGroup.set(groupKey, current);
      }
      for (const cartLine of cartLineDetails) {
        const itemId = cartLine.itemId!;
        const orderLine = orderLineDetails.get(itemId)!;
        const groupKey = orderLine.sku === null ? `item:${itemId}` : `sku:${orderLine.sku}`;
        const totals = totalsByGroup.get(groupKey)!;
        totals.actualUnits += cartLine.totalUnits;
        totals.lineTotalKrw += cartLine.lineTotalKrw;
      }
      for (const totals of totalsByGroup.values()) {
        if (
          totals.actualUnits !== totals.requiredUnits ||
          !Number.isSafeInteger(totals.actualUnits) ||
          !Number.isSafeInteger(totals.lineTotalKrw) ||
          totals.lineTotalKrw <= 0
        ) {
          return `examples[${exampleIndex}].expected clean assignments must satisfy each SKU group's required units and positive total price`;
        }
      }
    }
  }

  return null;
};
